import { UiNode, UiNodeInputAttributes } from '@ory/client';
import SsoButton from 'src/components/convenience/buttons/SsoButton';

type Provider = 'google' | 'microsoft' | 'apple';
type OidcAction = 'provider' | 'link' | 'unlink';

interface OidcFormButtonProps {
  node: UiNode;
  formAction: string;
  csrfToken: string;
  action?: OidcAction;
  label?: string;
}

const getProviderFromValue = (value: string): Provider | null => {
  if (value.includes('google')) return 'google';
  if (value.includes('microsoft')) return 'microsoft';
  if (value.includes('apple')) return 'apple';
  return null;
};

export const OIDC_ATTEMPTED_PROVIDER_KEY = 'oidc_attempted_provider';

const getProviderDisplayName = (provider: Provider): string => {
  switch (provider) {
    case 'google':
      return 'Google';
    case 'microsoft':
      return 'Microsoft';
    case 'apple':
      return 'Apple';
  }
};

const getDefaultLabel = (provider: Provider, action: OidcAction): string => {
  if (action === 'unlink') {
    return `Unlink ${getProviderDisplayName(provider)} Account`;
  }
  if (action === 'link') {
    return `Link ${getProviderDisplayName(provider)} Account`;
  }

  return `Continue with ${getProviderDisplayName(provider)}`;
};

export const OidcFormButton = ({
  node,
  formAction,
  csrfToken,
  action: actionOverride,
  label: labelOverride,
}: OidcFormButtonProps) => {
  const attrs = node.attributes as UiNodeInputAttributes;
  const providerValue = attrs.value as string;
  const nodeAction = attrs.name as OidcAction;

  const provider = getProviderFromValue(providerValue);
  if (!provider) return null;

  const action = actionOverride ?? nodeAction;
  const label = labelOverride ?? getDefaultLabel(provider, action);
  const isDestructive = action === 'unlink';
  const includeMethodInput = action !== 'provider';

  return (
    <form action={formAction} method="POST">
      <input type="hidden" name="csrf_token" value={csrfToken} />
      {includeMethodInput && <input type="hidden" name="method" value="oidc" />}
      <SsoButton
        provider={provider}
        variant={isDestructive ? 'destructive' : 'default'}
        type="submit"
        name={action}
        value={providerValue}
        fullWidth
        onClick={() =>
          localStorage.setItem(OIDC_ATTEMPTED_PROVIDER_KEY, getProviderDisplayName(provider))
        }
      >
        {label}
      </SsoButton>
    </form>
  );
};
